import { CollectionsContext } from '../../context/CollectionsContext';
import { OwnerType, ResourceType } from '../../types';
import { sendBqEvent } from './bigQuery';
import { sendMixpanelEvent } from './mixpanel';

interface CollectionBqEventProp {
    id: string;
    /**
     * If set as private is on = user, else = business
     */
    type: OwnerType;
    name: string;
    description?: string;
}

interface CollectionWithItemBqEventProp extends CollectionBqEventProp {
    item: {
        id: string;
        type: ResourceType;
    };
}

export const sendClickOnCollectActionsMenuEvent = ({ biContext }: { biContext: CollectionsContext['bi'] }) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'clicked_on_collect_actions_menu',
        page,
    });

    sendMixpanelEvent('Collect menu clicked', mixPanelPageProps(page));
};

export const sendAddItemToListEvent = ({
    biContext,
    gigId,
    sellerId,
    collection,
}: {
    biContext: CollectionsContext['bi'];
    gigId?: string;
    sellerId?: string;
    collection: CollectionWithItemBqEventProp;
}) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'list_item.added',
        page,
        gig: {
            id: gigId,
        },
        seller: {
            id: sellerId,
        },
        collection,
    });

    sendMixpanelEvent('Collection item added', mixPanelItemActionProps(page, collection));
};

export const sendRemoveItemFromListEvent = ({
    biContext,
    gigId,
    sellerId,
    collection,
}: {
    biContext: CollectionsContext['bi'];
    gigId?: string;
    sellerId?: string;
    collection: CollectionWithItemBqEventProp;
}) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'list_item.removed',
        page,
        gig: {
            id: gigId,
        },
        seller: {
            id: sellerId,
        },
        collection,
    });

    sendMixpanelEvent('Collection item removed', mixPanelItemActionProps(page, collection));
};

export const sendClickCreateNewListEvent = ({ biContext }: { biContext: CollectionsContext['bi'] }) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'clicked_on_create_list',
        page,
    });

    sendMixpanelEvent('Collect menu create collection clicked', mixPanelPageProps(page));
};

export const sendClickEditListEvent = ({ biContext }: { biContext: CollectionsContext['bi'] }) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'clicked_on_edit_list',
        page,
    });

    sendMixpanelEvent('Collect menu edit collection clicked', mixPanelPageProps(page));
};

export const sendClickDeletedListEvent = ({ biContext }: { biContext: CollectionsContext['bi'] }) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'clicked_on_delete_list',
        page,
    });

    sendMixpanelEvent('Collect menu delete collection clicked', mixPanelPageProps(page));
};

/**
 * Should be triggered after request success response
 */
export const sendListHasBeenCreatedEvent = ({
    biContext,
    gigId,
    sellerId,
    collection,
}: {
    biContext: CollectionsContext['bi'];
    gigId?: string;
    sellerId?: string;
    collection: CollectionWithItemBqEventProp;
}) => {
    const page = pageBqEventProp(biContext);

    sendBqEvent('user_action', {
        type: 'list.created',
        page,
        gig: {
            id: gigId,
        },
        seller: {
            id: sellerId,
        },
        collection,
    });

    sendMixpanelEvent('Collection created', mixPanelCollectionActionProps(page, collection));
};

/**
 * Should be triggered after request success response
 */
export const sendListHasBeenEditedEvent = ({
    biContext,
    collection,
    collectionBeforeChanges,
    itemsCount,
}: {
    biContext: CollectionsContext['bi'];
    collection: CollectionBqEventProp;
    collectionBeforeChanges: CollectionBqEventProp;
    itemsCount: number;
}) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'list.edit',
        page,
        collection,
    });

    sendMixpanelEvent(
        'Collection edited',
        mixPanelEditCollectionProps(page, collection, collectionBeforeChanges, itemsCount)
    );
};

/**
 * Should be triggered after request success response
 */
export const sendListHasBeenDeletedEvent = ({
    biContext,
    collection,
    itemsCount,
}: {
    biContext: CollectionsContext['bi'];
    collection: CollectionBqEventProp;
    itemsCount: number;
}) => {
    const page = pageBqEventProp(biContext);
    sendBqEvent('user_action', {
        type: 'list.deleted',
        page,
        collection,
    });

    sendMixpanelEvent('Collection deleted', mixPanelDeleteCollectionProps(page, collection, itemsCount));
};

const pageBqEventProp = (biContext: CollectionsContext['bi']) => ({
    name: biContext.source,
    component: {
        name: biContext.componentName,
    },
});

const mixPanelItemActionProps = (page, collection) => ({
    ...mixPanelCollectionActionProps(page, collection),
    'Resource Id': collection.item.id,
    'Resource type': collection.item.type,
});

const mixPanelEditCollectionProps = (page, collection, collectionBeforeChanges, itemsCount) => ({
    ...mixPanelCollectionActionProps(page, collection),
    'Has description': !!collection.description,
    'Number of saved items': itemsCount,
    'Has updated list name': collection.name !== collectionBeforeChanges.name,
    'Has updated list description': collection.description !== collectionBeforeChanges.description,
    'Has updated owner type': collection.type !== collectionBeforeChanges.type,
});

const mixPanelDeleteCollectionProps = (page, collection, itemsCount) => ({
    ...mixPanelCollectionActionProps(page, collection),
    'Has description': !!collection.description,
    'Number of saved items': itemsCount,
});

const mixPanelCollectionActionProps = (page, collection) => ({
    ...mixPanelPageProps(page),
    'Collection Id': collection.id,
    'Collection name': collection.name,
    'Owner type': collection.type,
});

const mixPanelPageProps = (page) => ({
    ...(page.name ? { 'Page Name': page.name } : {}),
    'Component name': page?.component?.name,
});
